import React, { useEffect, useRef, useState } from "react";
import { AfDataType } from "utils/TableColumnData";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useNavigate } from "react-router-dom";

import { ArrowDownIcon, ArrowsUpDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import Spinner from "utils/Spinner";
import AsyncSelect from "react-select/async";
import { TrainTrackingModal } from "components/TrainTrackingModal";

const TrainSchedule: React.FC<any> = (props) => {
  const ADT = AfDataType;
  const navigator = useNavigate();
  const [tableData, setTableData] = useState<any>([]);
  const [title, setTitle] = useState<any>("");

  const [trainCodes, setTrainCodes] = useState<any>([]);
  const [selectedFromStation, setSelectedFromStation] = useState<any>(null);
  const [selectedToStation, setSelectedToStation] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<any>(moment().format("YYYY-MM-DD"));

  const [sortStat, setSortStat] = useState<any>({});
  const [loading, setLoading] = useState(false);

  // Tracking modal state
  const [trackingModal, setTrackingModal] = useState<{
    isOpen: boolean;
    trainNo: string;
    trainName: string;
  }>({ isOpen: false, trainNo: "", trainName: "" });

  const openTrackingModal = (trainNo: string, trainName: string) => {
    setTrackingModal({ isOpen: true, trainNo, trainName });
  };

  const closeTrackingModal = () => {
    setTrackingModal({ isOpen: false, trainNo: "", trainName: "" });
  };

  // Fetch train codes on mount
  useEffect(() => {
    const fetchTrainCodes = async () => {
      try {
        const response = await fetch("https://api.airfield.express/train_codes.json");
        const data = await response.json();
        const formattedCodes = data.map((item: any) => ({
          label: `${item.name} - ${item.code}`,
          value: item.code,
          name: item.name,
        }));
        setTrainCodes(formattedCodes);
      } catch (error) {
        console.error("Error fetching train codes:", error);
      }
    };
    fetchTrainCodes();
  }, []);

  // Load options for AsyncSelect - returns promise with filtered results
  const loadStationOptions = (inputValue: string) => {
    return new Promise<any[]>((resolve) => {
      if (!inputValue || inputValue.length < 2) {
        resolve([]);
        return;
      }
      const filtered = trainCodes
        .filter((option: any) => option.label.toLowerCase().includes(inputValue.toLowerCase()))
        .slice(0, 10);
      resolve(filtered);
    });
  };

  // Calculate days offset for arrival (e.g., +1 for next day arrival)
  const calculateArrivalDayOffset = (departure: string, duration: string): number => {
    if (!departure || !duration) return 0;
    try {
      // Parse departure time: "20.10" → 20 hours, 10 minutes
      const [depHours, depMins] = departure.split(".").map(Number);
      // Parse duration: "11.25" → 11 hours, 25 minutes
      const [durHours, durMins] = duration.split(".").map(Number);
      
      const departureMinutes = depHours * 60 + depMins;
      const durationMinutes = durHours * 60 + durMins;
      const totalMinutes = departureMinutes + durationMinutes;
      
      // Days offset = how many times we cross midnight
      return Math.floor(totalMinutes / 1440); // 1440 = 24 * 60 minutes in a day
    } catch {
      return 0;
    }
  };

  // Parse train data from API response
  const parseTrainData = (responseText: string) => {
    const trains: any[] = [];
    const records = responseText.split("^");
    
    // Parse header to get searched destination code
    // Header format: ~PUNE~Pune Jn~MMCT~Mumbai Central~~2026-2-23-8-20-48~~~
    const headerFields = records[0].split("~");
    const searchedDestCode = headerFields[3]; // The destination station code user searched for
    
    // Parse train records (skip the first part which is header)
    for (let i = 1; i < records.length; i++) {
      const fields = records[i].split("~");
      if (fields.length > 12) {
        // Field 9 is the alighting station code for this journey
        const alightingStationCode = fields[9];
        const isDirect = alightingStationCode === searchedDestCode;
        const arrivalDayOffset = calculateArrivalDayOffset(fields[10], fields[12]);
        
        trains.push({
          trainNo: fields[0],
          trainName: fields[1],
          fromStation: fields[2],
          fromCode: fields[3],
          toStation: fields[4],
          toCode: fields[5],
          boardingStation: fields[6],
          boardingCode: fields[7],
          alightingStation: fields[8],
          alightingCode: fields[9],
          departure: fields[10],
          arrival: fields[11],
          duration: fields[12],
          runningDays: fields[13],
          isDirect: isDirect,
          arrivalDayOffset: arrivalDayOffset,
        });
      }
    }
    return trains;
  };

  // Get running days string
  const getRunningDays = (days: string) => {
    if (!days || days.length < 7) return "N/A";
    // erail API format: Mon=0, Tue=1, Wed=2, Thu=3, Fri=4, Sat=5, Sun=6
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const runningDays = dayNames.filter((_, i) => days[i] === "1");
    return runningDays.length === 7 ? "Daily" : runningDays.join(", ");
  };

  // Check if train runs on selected date
  const runsOnDate = (runningDays: string, date: string) => {
    if (!runningDays || runningDays.length < 7) return true; // Show if no data
    // moment().day() returns 0=Sun, 1=Mon, ... 6=Sat
    // erail API format: Mon=0, Tue=1, Wed=2, Thu=3, Fri=4, Sat=5, Sun=6
    const momentDay = moment(date).day(); // 0=Sun, 1=Mon, ..., 6=Sat
    // Convert moment day to API index: Sun(0)->6, Mon(1)->0, Tue(2)->1, etc.
    const apiIndex = momentDay === 0 ? 6 : momentDay - 1;
    return runningDays[apiIndex] === "1";
  };

  const { isFetching, refetch } = useQuery(
    ["train_list", selectedFromStation?.value, selectedToStation?.value],
    async () => {
      const response = await fetch(
        `https://erail.in/rail/getTrains.aspx?Station_From=${selectedFromStation?.value}&Station_To=${selectedToStation?.value}&DataSource=0&Language=0&Cache=true`
      );
      return response.text();
    },
    {
      keepPreviousData: true,
      enabled: false,
      onSuccess(data) {
        if (data) {
          const parsedTrains = parseTrainData(data);
          // Filter trains that run on the selected date
          const filteredTrains = parsedTrains.filter((train) => runsOnDate(train.runningDays, selectedDate));
          setTableData(filteredTrains);
          setTitle(`${selectedFromStation?.name || selectedFromStation?.value} to ${selectedToStation?.name || selectedToStation?.value} on ${moment(selectedDate).format("DD MMM YYYY (ddd)")}`);
        } else {
          setTableData([]);
        }
      },
      onError: (data) => {
        setTableData([]);
      },
    }
  );

  const __handleSubmit = async () => {
    if (!selectedFromStation?.value || !selectedToStation?.value) {
      alert("Please select both From and To stations");
      return;
    }
    refetch();
  };

  const tableRef = useRef(null);

  const GET_SORT_ICON = (key, info) => {
    switch (sortStat[key]) {
      case "asc":
        return (
          <ArrowDownIcon
            className="h-4 inline-block"
            onClick={() => {
              handleSort(key, info);
            }}
          />
        );
      case "desc":
        return (
          <ArrowUpIcon
            className="h-4 inline-block"
            onClick={() => {
              handleSort(key, info);
            }}
          />
        );
      default:
        return (
          <ArrowsUpDownIcon
            className="h-4 inline-block"
            onClick={() => {
              handleSort(key, info);
            }}
          />
        );
    }
  };

  const handleSort = (key, info) => {
    let data = [...tableData];
    let order = sortStat[key] == "asc" ? "desc" : "asc";

    if (key == "_invoice_no_c_b.isFullPaid") {
      data = data.sort((a, b) => {
        if (order == "asc") {
          return -a["isActive"] + b["isActive"];
        } else {
          return -b["isActive"] + a["isActive"];
        }
      });
    }

    let sortedData = data.sort((a, b) => {
      // Special handling for runningDays - "Daily" comes first in asc, last in desc
      if (key === "runningDays") {
        const displayA = getRunningDays(a.runningDays);
        const displayB = getRunningDays(b.runningDays);
        
        // Handle "Daily" specially
        if (displayA === "Daily" && displayB !== "Daily") {
          return order === "asc" ? -1 : 1;
        }
        if (displayB === "Daily" && displayA !== "Daily") {
          return order === "asc" ? 1 : -1;
        }
        // Both are Daily or neither - sort alphabetically
        if (displayA < displayB) return order === "asc" ? -1 : 1;
        if (displayA > displayB) return order === "asc" ? 1 : -1;
        return 0;
      }
      
      // Special handling for isDirect - Direct comes first in asc, Via first in desc
      if (key === "isDirect") {
        if (a.isDirect && !b.isDirect) return order === "asc" ? -1 : 1;
        if (!a.isDirect && b.isDirect) return order === "asc" ? 1 : -1;
        return 0;
      }
      
      // Extract the value using the keyPath (e.g., "invoice_data.idpaid")
      const valueA = key.split(".").reduce((obj, key) => obj[key], a);
      const valueB = key.split(".").reduce((obj, key) => obj[key], b);
      // Handle sorting for different data types
      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0; // If equal
    });
    let _sortStat = { ...sortStat };
    _sortStat[key] = order;
    setSortStat(_sortStat);
    setTableData(sortedData);
  };

  return (
    <>
      {isFetching && <Spinner />}
      <h1 className="text-center mb-2 mt-1 text-lg font-semibold ">
        Train Schedule --- <u>{title}</u>
      </h1>
      <div className="px-4 border-[1px] py-2  rounded-md">
        <div className="flex flex-col items-center">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            
            <div className="">
              <label className="block mb-0.5 text-sm font-medium text-gray-900">From Station</label>
              <AsyncSelect
                className={`w-64 bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block`}
                loadOptions={loadStationOptions}
                onChange={(e) => setSelectedFromStation(e)}
                value={selectedFromStation}
                placeholder="Type 2+ chars to search..."
                isClearable={true}
                cacheOptions
                defaultOptions={[]}
                noOptionsMessage={({ inputValue }) => 
                  inputValue.length < 2 ? "Type at least 2 characters..." : "No stations found"
                }
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const temp = selectedFromStation;
                setSelectedFromStation(selectedToStation);
                setSelectedToStation(temp);
              }}
              className="mt-5 p-2 rounded-full bg-gray-200 hover:bg-gray-300 focus:outline-none"
              title="Swap stations"
            >
              <ArrowsUpDownIcon className="h-5 w-5 rotate-90" />
            </button>
            <div className="">
              <label className="block mb-0.5 text-sm font-medium text-gray-900">To Station</label>
              <AsyncSelect
                className={`w-64 bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block`}
                loadOptions={loadStationOptions}
                onChange={(e) => setSelectedToStation(e)}
                value={selectedToStation}
                placeholder="Type 2+ chars to search..."
                isClearable={true}
                cacheOptions
                defaultOptions={[]}
                noOptionsMessage={({ inputValue }) => 
                  inputValue.length < 2 ? "Type at least 2 characters..." : "No stations found"
                }
              />
            </div>

            <div className="">
              <label className="block mb-0.5 text-sm font-medium text-gray-900">Travel Date</label>
              <input
                type="date"
                className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
                placeholder="Select Date"
                name="selectedDate"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>

            <button
              type="button"
              onClick={__handleSubmit}
              className="ml-2 mt-2 rounded-md px-4 py-2 text-sm font-medium text-white bg-green-500 border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      <div className="px-4">
        <table className="w-full table- mb-8 text-xl" id="myTable" ref={tableRef}>
          <thead className="sticky top-0 bg-gray-300">
            <tr>
              <th className="border border-slate-200 px-1">Sr.</th>
              <th className="border border-slate-200 px-1">Type {GET_SORT_ICON("isDirect", "")}</th>
              <th className="border border-slate-200 px-1">Train No {GET_SORT_ICON("trainNo", "")}</th>
              <th className="border border-slate-200 px-1">Train Name {GET_SORT_ICON("trainName", "")}</th>
              <th className="border border-slate-200 px-1">Arrives At {GET_SORT_ICON("alightingStation", "")}</th>
              <th className="border border-slate-200 px-1 whitespace-nowrap">Departure {GET_SORT_ICON("departure", "")}</th>
              <th className="border border-slate-200 px-1 whitespace-nowrap">Arrival {GET_SORT_ICON("arrival", "")}</th>
              <th className="border border-slate-200 px-1">Duration {GET_SORT_ICON("duration", "")}</th>
              <th className="border border-slate-200 px-1">Running Days {GET_SORT_ICON("runningDays", "")}</th>
              <th className="border border-slate-200 px-1">Track</th>
            </tr>
          </thead>
          <tbody>
            {tableData &&
              tableData.map((r, i) => {
                return (
                  <tr key={"train-" + i} className={`${i % 2 == 0 ? "bg-blue-400" : "bg-blue-200"}`}>
                    <td className="border border-slate-200 px-2 py-0.5">{i + 1}</td>
                    <td className={`border border-slate-200 px-2 py-0.5 text-center font-semibold ${r.isDirect ? "text-green-800" : "text-orange-700"}`}>
                      {r.isDirect ? "Direct" : "Via"}
                    </td>
                    <td className="border border-slate-200 px-2 py-0.5">{r.trainNo}</td>
                    <td className="border border-slate-200 px-2 py-0.5">{r.trainName}</td>
                    <td className="border border-slate-200 px-2 py-0.5 text-sm">
                      {r.alightingStation} ({r.alightingCode})
                    </td>
                    <td className="border border-slate-200 px-2 py-0.5">{r.departure}</td>
                    <td className="border border-slate-200 px-2 py-0.5">
                      {r.arrival}
                      {r.arrivalDayOffset > 0 && (
                        <sup className="ml-1 text-red-600 font-semibold">+{r.arrivalDayOffset}</sup>
                      )}
                    </td>
                    <td className="border border-slate-200 px-2 py-0.5">{r.duration}</td>
                    <td className="border border-slate-200 px-2 py-0.5">{getRunningDays(r.runningDays)}</td>
                    <td className="border border-slate-200 px-2 py-0.5 text-center">
                      <button
                        onClick={() => openTrackingModal(r.trainNo, r.trainName)}
                        className="px-3 py-1 text-sm bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-sm hover:shadow-md"
                      >
                        Track
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Live Tracking Modal */}
      <TrainTrackingModal
        isOpen={trackingModal.isOpen}
        onClose={closeTrackingModal}
        trainNo={trackingModal.trainNo}
        trainName={trackingModal.trainName}
      />
    </>
  );
};

export default TrainSchedule;
